import { jsx, css } from '@emotion/react';
import { useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Switch, withRouter, NavLink } from 'react-router-dom';

import { CalculatorCellulose } from './CalculatorCellulose';
import { CalculatorFiberglass } from './CalculatorFiberglass';
import { CalculatorSprayFoam } from './CalculatorSprayFoam';
import { ComparisonTable } from './ComparisonTable';
import { ProductSelector } from './ProductSelector';
import { connectVariant } from './SprayfoamConnectors';
import TrainingVideos from './TrainingVideos';
import withDefaultPrevented from '../../withDefaultPrevented';
import Modal from '../Modal';
import AttentionBarButtons from '../OC/oc-attention-bar-buttons';
import Container from '../OC/oc-container';
import ContentAndImage from '../OC/oc-content-and-image';
import Cta from '../OC/oc-cta';
import Divider from '../OC/oc-divider';
import DownloadLink from '../OC/oc-download-link';
import Dropdown from '../OC/oc-dropdown';
import MediaObject from '../OC/oc-media-object';
import Section from '../OC/sections/oc-section';
import StickyNav from '../OC/sections/oc-sticky-nav';
import { store } from '../Store';
import { localizedBaseUrl, OffsetAnchor } from '../Utilities.jsx';
import { connectLocation } from '../location/LocationConnectors';

export default ({ baseUrl }) => {
  const [modalOpen, changeModalState] = useState(true);
  const stickyNavCta = (
    <Cta
      className="oc-cta-button"
      href="#calculator"
      data-track="learn-more"
      data-track-element-location="sticky nav CTA"
      data-track-destination="Calculate Your Savings"
    >
      Calculate Your Savings
    </Cta>
  );

  return (
    <Provider store={store}>
      <StickyNav cta={stickyNavCta}>
        <WhyProPink heading="Why ProPink?" />
        <ProPinkL77VsCompetition />
        <BrowserRouter heading="Compare" basename={`${localizedBaseUrl(baseUrl)}/`}>
          <Switch>
            <Route path={['/ulf-calculator', '/propink-vs-fiberglass-competition']} exact>
              <ProductSelector />
              <ProPinkFiberglassVsCompetition />
              <OffsetAnchor id="calculator" />
              <Container maxWidth="1170">
                <CalculatorFiberglass />
              </Container>
              <ProPinkFiberglassComparisonTable />
            </Route>
            <Route path="/propink-vs-cellulose">
              <ProductSelector />
              <ProPinkCelluloseVsCompetition />
              <OffsetAnchor id="calculator" />
              <Container maxWidth="1170">
                <CalculatorCellulose />
              </Container>
              <ProPinkCelluloseComparisonTable />
            </Route>
          </Switch>
        </BrowserRouter>
        <Container maxWidth="1170">
          <Divider />
        </Container>
        <TrainingVideos heading="Videos" />
        <Container maxWidth="1170">
          <Divider />
        </Container>
        <ProPinkFeatures heading="Features" />
        <Container maxWidth="1170">
          <Divider />
        </Container>
        <Literature heading="Literature" />
        <Container maxWidth="1170">
          <Divider />
        </Container>
        <TechnicalInformation heading="Technical Information" />
        <FASRSplash />
        <div id="modalTarget" />
      </StickyNav>
    </Provider>
  );
};

// Shared, "static" components (i.e. not changing for each variant)
export const Header = () => <div>Header</div>;
export const Navigation = () => <div>Navigation</div>;

const whyProPinkStyles = css`
  margin-top: 40px;

  @media (min-width: 1200px) {
    margin-top: 72px;
  }
`;

const viewProductCtaStyles = css`
  margin-bottom: 8px;
`;

export const WhyProPink = () => (
  <Section css={whyProPinkStyles} anchor="why-propink-">
    <ContentAndImage video="QD6kqJKHIgo" ratio="1-1">
      <h5 className="prehead">Why PROPINK® Insulation?</h5>
      <h2>High Performance Insulation For High Performance Building</h2>

      <p>
        Owens Corning ProPink® L77 Loosefill Insulation offers outstanding coverage, fast application and exceptional
        R-value per square inch when compared to other fiberglass insulation products. Its simplicity and consistency
        allows installers to use one product for walls, attics, cathedral ceilings and floors. It’s also one of the few
        insulation solutions that allows you to dial in exact R-values for wall installations, helping you deliver a
        customized package that meets builders’ targets the first time, every time.
      </p>

      <div>
        <Cta
          href="#literature"
          color="white"
          css={viewProductCtaStyles}
          analytics={{
            track: 'learn-more',
            destination: 'ProPink L77 Product Literature',
          }}
        >
          View Product Literature
        </Cta>
      </div>

      <Cta
        href="/insulation/residential/propink-complete"
        color="white"
        data-track="learn-more"
        data-track-destination="oc | insulation | residential | propink-complete"
      >
        ProPink® Complete™ System
      </Cta>
    </ContentAndImage>
  </Section>
);

export const ProPinkFeatures = () => {
  const listColumns = css`
    list-style: none;
    list-style-image: url(https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png);
    columns: 2;
    column-gap: 48px;
    font-size: 14px;
    margin-bottom: 24px;

    @media (min-width: 1200px) {
      font-size: 16px;
    }

    li {
      break-inside: avoid-column;
      margin-bottom: 16px;
    }
  `;

  return (
    <Container maxWidth="1170">
      <Section anchor="features">
        <h2>ProPink® L77 Product Features</h2>

        <ul css={listColumns}>
          <li>
            <strong>Highest Coverage</strong> - Provides the highest yield per bag of any loosefill insulation
            available. In fact, one 32-lb bag of Owens Corning® insulation can cover 77 square feet at R-30.
          </li>
          <li>
            <strong>Exceptional Thermal Protection</strong> - Completely fills the cavity and eliminates gaps that
            require additional handwork.
          </li>
          <li>
            <strong>Design Integrity</strong> - Offers virtually no settling, enabling you to achieve high-quality,
            inspection-ready installs.
          </li>
          <li>
            <strong>Excellent Sustainability</strong> - Formaldehyde-free, GREENGUARD® Gold Certified, and certified 55%
            recycled content.
          </li>
          <li>
            <strong>NFPA 13 Standard</strong> - Meets standards for non-combustible insulation as an alternative to
            mid-floor sprinklers.
          </li>
          <li>Non combustible non-corrosive and validated to be formaldehyde free.&sup1;</li>
          <li>
            Able to be blown into walls at 2.5 lb density to provide equivalent airflow reduction and create greater
            R-value than cellulose in walls.
          </li>
          <li>Resistance to moisture, fungus and mold growth.&sup2;</li>
        </ul>

        <ol css={footnotes}>
          <li>Owens Corning® L77 ULF is validated formaldehyde free by the UL Environment.</li>
          <li>
            As manufactured fiberglass insulation is resistant to mold growth. (ASTM C1338-96 Fungi Resistance of
            Insulation Materials and Facings.) However, mold growth can occur on building materials, including
            insulation, when it becomes contaminated with organic material and when water is present. To avoid mold
            growth on fiber glass insulation, remove any water that has accumulated and correct or repair the source of
            that water as soon as possible. Insulation that has become wet should be inspected for evidence of residual
            moisture and contamination, and any insulation that is contaminated should be promptly removed and replaced.
          </li>
        </ol>
      </Section>
    </Container>
  );
};

const twoUp = css`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 30px;
    row-gap: 24px;
  }
`;

const threeUp = css`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 30px;
    row-gap: 24px;
  }
`;

const fourUp = css`
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 30px;
    row-gap: 48px;
  }
`;

export const ProPinkL77VsCompetition = () => (
  <Container gray padding>
    <h2 className="body-font" style={{ marginBottom: 24 }}>
      Why PROPINK® L77 vs. the Competition?
    </h2>

    <div css={threeUp}>
      <MediaObject
        img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1602785731/insulation/residential/ulf-calculator/icon-less-time.svg"
        alt="Less Time = More Money"
      >
        <h3 className="body-font">Less Time = More Money</h3>
        <p>Designed for easy install with nearly any blowing machine and can reduce blow time.</p>
      </MediaObject>

      <MediaObject
        img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1602785731/insulation/residential/ulf-calculator/icon-versatility.svg"
        alt="Incredible Versatility & Efficiency"
      >
        <h3 className="body-font">Incredible Versatility & Efficiency</h3>
        <p>
          Certified for use in walls, attics, cathedral ceilings and floors in new construction and retrofit
          applications so you can use one product for many jobs.
        </p>
      </MediaObject>

      <MediaObject
        img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1602785731/insulation/residential/ulf-calculator/icon-improved-productivity.svg"
        alt="Improved Productivity"
      >
        <h3 className="body-font">Improved Productivity</h3>
        <p>Reduces blowing time by an average of 15%, enhancing productivity job after job.</p>
      </MediaObject>

      <MediaObject
        img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1602785731/insulation/residential/ulf-calculator/icon-pass-code.svg"
        alt="Pass Code with Confidence"
      >
        <h3 className="body-font">Pass Code with Confidence</h3>
        <p>
          With the help of our exclusive Inspect-R® Gauge tool, you can accurately field verify the R-value of wall
          installations.
        </p>
      </MediaObject>

      <MediaObject
        img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1602785731/insulation/residential/ulf-calculator/icon-comfort.svg"
        alt="Installer Comfort"
      >
        <h3 className="body-font">Installer Comfort</h3>
        <p>Owens Corning® products are designed to create less dust and less itch.</p>
      </MediaObject>
    </div>
  </Container>
);

const comparisonTable = css`
  border-collapse: collapse;
  font-size: 14px;
  width: 100%;

  @media (min-width: 1200px) {
    margin-bottom: 16px;
  }

  tbody tr:nth-child(odd) td {
    background: #f8f8f8;
  }

  th {
    padding: 11px 9px;
    border-top: 2px solid #bcbcbc;
    border-bottom: 2px solid #bcbcbc;
    border-left: 1px solid #bcbcbc;

    &:first-child {
      border-left: 0;
    }
  }

  td {
    border-bottom: 1px solid #bcbcbc;
    padding: 11px 9px;
    border-left: 1px solid #bcbcbc;

    &:first-child {
      border-left: 0;
    }

    &.center {
      text-align: center;
    }
  }

  .colProPink,
  .pink {
    color: #d40f7d;
    font-weight: 700;
  }

  .tableSubhead {
    text-transform: uppercase;
    font-weight: 700;
  }

  .tableIndent {
    padding-left: 29px;
  }
`;

const technicalTable = css`
  ${comparisonTable}

  th {
    text-align: left;
  }

  td {
    text-align: center;
    min-width: 90px;
  }
`;

const footnotes = css`
  list-style: none;
  counter-reset: localCounter;
  font-size: 12px;
  padding-left: 0;

  li {
    counter-increment: localCounter;
    margin-bottom: 3px;

    &::before {
      content: counter(localCounter);
      font-size: 8px;
      margin-right: 3px;
      position: relative;
      bottom: 2px;
    }
  }
`;

const narrowHeading = css`
  @media (min-width: 1200px) {
    max-width: 970px;
    margin-right: auto;
    margin-left: auto;
  }
`;

const charts = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;

  @media (max-width: 767px) {
    margin-bottom: 32px;
  }

  img {
    align-self: center;
  }
`;

export const ProPinkFiberglassComparisonTable = () => (
  <Container maxWidth="970">
    <ComparisonTable>
      <table className="threeUp">
        <thead>
          <tr>
            <th>
              <span className="sr-only">Recognitions</span>
            </th>
            <th className="colProPink">Owens Corning ProPink L77</th>
            <th>Other Competitive Loosefill Products</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>#1 Handleability for Contractors &sup1;</td>
            <td className="center colProPink">
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png"
                alt="Check"
              />
            </td>
            <td>
              <span className="sr-only">no value</span>
            </td>
          </tr>
          <tr>
            <td>#1 Most Trusted by Builders &sup2;</td>
            <td className="center colProPink">
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png"
                alt="Check"
              />
            </td>
            <td>
              <span className="sr-only">no value</span>
            </td>
          </tr>
          <tr>
            <td>#1 Most Recognized by Homeowners &sup3;</td>
            <td className="center colProPink">
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png"
                alt="Check"
              />
            </td>
            <td>
              <span className="sr-only">no value</span>
            </td>
          </tr>
          <tr>
            <td>Accurately field verify R-Value &#x2074;</td>
            <td className="center colProPink">
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png"
                alt="Check"
              />
            </td>
            <td>
              <span className="sr-only">no value</span>
            </td>
          </tr>
          <tr>
            <td>Highest Recycled Content &#x2075;</td>
            <td className="center colProPink">
              <img
                src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603820288/insulation/residential/ulf-calculator/icon-checkmark.png"
                alt="Check"
              />
            </td>
            <td>
              <span className="sr-only">no value</span>
            </td>
          </tr>
        </tbody>
      </table>
    </ComparisonTable>

    <ol css={footnotes}>
      <li>
        Builder Brand Use Study, Hanley Wood, 2017;{' '}
        <a href="http://www.builderonline.com/products/2017-builder-brand-use-study-results_o">
          www.builderonline.com/products/2017-builder-brand-use-study-results_o
        </a>
      </li>
      <li>
        Home Innovation Research Labs Builder Panel, April 2019. Owens Corning sponsored research, Home Innovation
        Research Labs builder panel, April 2019.
      </li>
      <li>2013 Homeowner Brand Awareness Study by Owens Corning</li>
      <li>Field Verify is using the Inspect R Gauge</li>
      <li>Highest recycled content is per manufacturer product guides, 2020</li>
    </ol>
  </Container>
);

export const ProPinkFiberglassVsCompetition = () => (
  <div>
    <Container maxWidth="970">
      <h2>
        The Cheapest Insulation Isn’t <span className="nowrap">Always Cheaper.</span>
        <br /> See How Fiberglass Stacks Up.
      </h2>
    </Container>

    <Container maxWidth="1170">
      <div css={threeUp}>
        <div css={charts}>
          <h4>Bags of loosefill required for 100,000 sq. ft. of attic coverage at R-30*</h4>

          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_350/v1603807616/insulation/residential/ulf-calculator/chart-bags-of-loosefill.png"
            srcSet="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_700/v1603807616/insulation/residential/ulf-calculator/chart-bags-of-loosefill.png 2x"
            className="img-responsive"
            alt="Bar graph of bags of loosefill required for 100,000 square feet of attic coverage at R-30. ProPink L77 is determined at 1220 bags, Knauf JETSTREAM at 1360 bags, CertainTeed INSULSAFE SP at 1379 bags, and Johns Manville CLIMATE PRO at 1370 bags."
          />
        </div>

        <div css={charts}>
          <h4>Inches of Insulation to achieve R-30*</h4>

          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_350/v1657034952/insulation/residential/ulf-calculator/chart-inches-r30.jpg"
            srcSet="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_700/v1657034952/insulation/residential/ulf-calculator/chart-inches-r30.jpg 2x"
            className="img-responsive"
            alt="Bar graph of Inches of Insulation to achieve R-30. ProPink L77 is determined at 10.75 inches, Knauf JETSTREAM at 10.38 inches, CertainTeed INSULSAFE SP at 11.5 inches, and Johns Manville CLIMATE PRO at 11.10 inches."
          />
        </div>

        <div css={charts}>
          <h4>Blow truck capacity (assuming 150 bags on blowing truck sq. ft. of insulation R-30)*</h4>

          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_350/v1603807616/insulation/residential/ulf-calculator/chart-blow-truck-capacity.png"
            srcSet="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_700/v1603807616/insulation/residential/ulf-calculator/chart-blow-truck-capacity.png 2x"
            className="img-responsive"
            alt="Bar graph of blow truck capacity (assuming 150 bags on blowing truck square feet of insulation R-30). ProPink L77 is determined at 11,550 square feet, Knauf JETSTREAM at 10,995 square feet, CertainTeed INSULSAFE SP at 10,875 square feet, and Johns Manville CLIMATE PRO at 10,950 square feet."
          />
        </div>
      </div>

      <p>
        <small>* Source: pulled from current manufacturer’s labels</small>
      </p>
    </Container>
  </div>
);

export const ProPinkCelluloseComparisonTable = () => (
  <Container maxWidth="970">
    <ComparisonTable>
      <table className="threeUp">
        <thead>
          <tr>
            <th>
              <span className="sr-only">Variables</span>
            </th>
            <th className="colProPink">Owens Corning ProPink L77</th>
            <th>Cellulose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="tableSubhead" colSpan="3">
              Productivity
            </td>
          </tr>
          <tr>
            <td className="tableIndent">Job Efficiency</td>
            <td>
              Complete <span className="pink">more jobs per day</span> with 2x wall coverage per truckload of product
            </td>
            <td>Jobs per day is more limited based on the capacity the product occupies in the truck</td>
          </tr>
          <tr>
            <td className="tableIndent">Wall Installation/drying time required</td>
            <td>
              <span className="pink">Does not require drying time</span>; no water added
            </td>
            <td>
              Wet applied cellulose can add up to 100 gallons of water to each job installed, requiring 24 hours or more
              to dry
            </td>
          </tr>
          <tr>
            <td className="tableSubhead" colSpan="3">
              Performance
            </td>
          </tr>
          <tr>
            <td className="tableIndent">Attic Performance</td>
            <td>
              Virtually <span className="pink">no settling</span>
            </td>
            <td>Can settle up to 20%</td>
          </tr>
          <tr>
            <td className="tableIndent">Code & Thermal Performance</td>
            <td>
              <span className="pink">Meets R-15 in wall cavities</span>
            </td>
            <td>Caps out at R-13 in a 2x4 wall</td>
          </tr>
          <tr>
            <td className="tableSubhead" colSpan="3">
              Safety
            </td>
          </tr>
          <tr>
            <td className="tableIndent">Fire Resistance</td>
            <td>
              Naturally <span className="pink">fire-resistant and non-combustible</span>
            </td>
            <td>Contains up to 20% chemically based fire retardants</td>
          </tr>
          <tr>
            <td className="tableIndent">Health</td>
            <td>
              Fiberglass insulation fibers <span className="pink">have been tested for safety</span> and designed to
              dissove in the body if inhaled
            </td>
            <td>No health testing for cellulose insulation fibers</td>
          </tr>
        </tbody>
      </table>
    </ComparisonTable>
  </Container>
);

export const ProPinkCelluloseVsCompetition = () => (
  <div>
    <Container maxWidth="970">
      <h2>
        The Cheapest Insulation Isn’t <span className="nowrap">Always Cheaper.</span>
        <br /> See How Cellulose Stacks Up.
      </h2>
    </Container>

    <Container maxWidth="570">
      <div css={twoUp}>
        <div css={charts}>
          <h4>Bags Needed for Average Attic*</h4>

          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_175/v1657034856/insulation/residential/ulf-calculator/chart-bags-needed-cellulose.jpg"
            srcSet="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_350/v1657034856/insulation/residential/ulf-calculator/chart-bags-needed-cellulose.jpg 2x"
            className="img-responsive"
            alt="Bar graph of bags needed for average attic. ProPink L77 is determined at 2 bags and GreenFiberINS735 at 5 bags"
          />
        </div>

        <div css={charts}>
          <h4>Coverage per Truckload</h4>

          <img
            src="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_175/v1603907239/insulation/residential/ulf-calculator/chart-coverage-per-truckload.png"
            srcSet="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto,w_350/v1603907239/insulation/residential/ulf-calculator/chart-coverage-per-truckload.png 2x"
            className="img-responsive"
            alt="Bar graph of coverage per truckload. ProPink L77 is determined at 2 times the coverage and GreenFiberINS735 data is at 1 time"
          />
        </div>
      </div>

      <p>
        <small>* Based on a 2,000 sq ft attic at R-49</small>
      </p>
    </Container>
  </div>
);

// Those two probably should be hooked up to MDMS through existing component
export const Literature = () => (
  <Container maxWidth="1170">
    <Section anchor="literature">
      <h2>SPECIFICATIONS AND LITERATURE</h2>

      <Container>
        <div css={threeUp}>
          <MediaObject
            img="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603892898/insulation/residential/ulf-calculator/brochure-l77-product-brochure.png"
            alt="ProPink L77 Product Brochure"
          >
            <h3 className="body-font">ProPink L77 Product Brochure</h3>
            <DownloadLink
              href="http://owenscorning.com/dms/44426"
              fileType="pdf"
              data-track="file-download"
              data-track-file-name="ProPink L77 Product Brochure"
              data-track-file-type="PDF"
              data-track-doc-type="Brochure"
            >
              PDF | 2.2 MB
            </DownloadLink>
          </MediaObject>
          <MediaObject
            img="https://imagecdn.owenscorning.com/ocimages/image/upload/v1618855006/insulation/residential/ulf-calculator/Cellulose%20vs.%20Fiberglass%20Comparison%20Sell%20Sheet.png"
            alt="Cellulose vs. Fiberglass Comparison Sell Sheet"
          >
            <h3 className="body-font">Cellulose vs. Fiberglass Comparison Sell Sheet</h3>
            <DownloadLink
              href="https://owenscorning.com/dms/10022309"
              fileType="pdf"
              data-track="file-download"
              data-track-file-name="Cellulose vs. Fiberglass Comparison Sell Sheet"
              data-track-file-type="PDF"
              data-track-doc-type="Brochure"
            >
              PDF | 1.7 MB
            </DownloadLink>
          </MediaObject>
          <MediaObject
            img="https://imagecdn.owenscorning.com/ocimages/image/upload/f_auto/v1603892898/insulation/residential/ulf-calculator/brochure-l77-contractor-features-and-benefits.png"
            alt="ProPink L77 Contractor Features and Benefits"
          >
            <h3 className="body-font">ProPink L77 Contractor Features and Benefits</h3>
            <DownloadLink
              href="http://owenscorning.com/dms/10019259"
              fileType="pdf"
              data-track="file-download"
              data-track-file-name="ProPink L77 Contractor Features and Benefits"
              data-track-file-type="PDF"
              data-track-doc-type="Brochure"
            >
              PDF | 3.6 MB
            </DownloadLink>
          </MediaObject>
        </div>
      </Container>

      <Container maxWidth="970">
        <Dropdown label="Data Sheets (1)" dataTrackAccordion="Data Sheets">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/43942"
                data-track="file-download"
                data-track-file-name="PROPINK® L77 Product Data Sheet"
                data-track-file-type="PDF"
                data-track-doc-type="Data Sheets"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK® L77 Product Data Sheet
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Sell Sheets (4)" dataTrackAccordion="Sell Sheets">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10019259"
                data-track="file-download"
                data-track-file-name="PROPINK® Complete Contractor Sell Sheet"
                data-track-file-type="PDF"
                data-track-doc-type="Sell Sheets"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK® Complete Contractor Sell Sheet
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10019258"
                data-track="file-download"
                data-track-file-name="PROPINK® Complete Builder Publication"
                data-track-file-type="PDF"
                data-track-doc-type="Technical Documents"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK® Complete Builder Publication
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10023621"
                data-track="file-download"
                data-track-file-name="Blown in Fiberglas Homeowner"
                data-track-file-type="PDF"
                data-track-doc-type="Technical Documents"
                target="_blank"
                rel="noreferrer"
              >
                Blown in Fiberglas Homeowner
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10023919"
                data-track="file-download"
                data-track-file-name="Attic Insulation Sell Sheet"
                data-track-file-type="PDF"
                data-track-doc-type="Sell Sheets"
                target="_blank"
                rel="noreferrer"
              >
                Attic Insulation Sell Sheet
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Brochures (2)" dataTrackAccordion="Brochures">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/44426"
                data-track="file-download"
                data-track-file-name="PROPINK® L77 Product Brochure"
                data-track-file-type="PDF"
                data-track-doc-type="Brochure"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK® L77 Product Brochure
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10023621"
                data-track="file-download"
                data-track-file-name="ProPink L77 Homeowner Features and Benefits"
                data-track-file-type="PDF"
                data-track-doc-type="Brochure"
                target="_blank"
                rel="noreferrer"
              >
                ProPink L77 Homeowner Features and Benefits
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Guide Specifications (1)" dataTrackAccordion="Guide Specifications">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10019379"
                data-track="file-download"
                data-track-file-name="Fiberglass, Acoustical, Interior Partition Insulation Guide Specifications"
                data-track-file-type="PDF"
                data-track-doc-type="Specification Sheet"
                target="_blank"
                rel="noreferrer"
              >
                Fiberglass, Acoustical, Interior Partition Insulation Guide Specifications
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Warranty (1)" dataTrackAccordion="Warranty">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10017808"
                data-track="file-download"
                data-track-file-name="Loose Fill Warranty"
                data-track-file-type="PDF"
                data-track-doc-type="Warranty"
                target="_blank"
                rel="noreferrer"
              >
                Loose Fill Warranty
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Installation Instructions (3)" dataTrackAccordion="Installation Instructions">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/44114"
                data-track="file-download"
                data-track-file-name="PROPINK® L77 Application Instructions"
                data-track-file-type="PDF"
                data-track-doc-type="Installation Instructions"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK® L77 Application Instructions
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10011213"
                data-track="file-download"
                data-track-file-name="PROPINK®L77 Machine Settings"
                data-track-file-type="PDF"
                data-track-doc-type="Technical Documents"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK®L77 Machine Settings
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10023876"
                data-track="file-download"
                data-track-file-name="PROPINK®L77 Coverage and Troubleshooting"
                data-track-file-type="PDF"
                data-track-doc-type="Technical Documents"
                target="_blank"
                rel="noreferrer"
              >
                PROPINK®L77 Coverage and Troubleshooting
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown
          label="Environmental Product Declarations (2)"
          dataTrackAccordion="Environmental Product Declarations"
        >
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10018099"
                data-track="file-download"
                data-track-file-name="Environmental Product Declaration - Unbonded Loosefill"
                data-track-file-type="PDF"
                data-track-doc-type="Environmental Product Declaration"
                target="_blank"
                rel="noreferrer"
              >
                Environmental Product Declaration - Unbonded Loosefill
              </a>
            </li>
            <li>
              <a
                href="http://owenscorning.com/dms/10018102"
                data-track="file-download"
                data-track-file-name="EPD Transparency Brief - Unbonded Loosefill"
                data-track-file-type="PDF"
                data-track-doc-type="Environmental Product Declaration"
                target="_blank"
                rel="noreferrer"
              >
                EPD Transparency Brief - Unbonded Loosefill
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Certifications (1)" dataTrackAccordion="Certifications">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10021334"
                data-track="file-download"
                data-track-file-name="SCS Certificate - 55 Percent"
                data-track-file-type="PDF"
                data-track-doc-type="Certification"
                target="_blank"
                rel="noreferrer"
              >
                SCS Certificate - 55 Percent
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="LEED (1)" dataTrackAccordion="LEED">
          <ul>
            <li>
              <a
                href="/en-us/insulation/commercial#leed"
                data-track="file-download"
                data-track-file-name="LEED Request Form"
                data-track-file-type="PDF"
                data-track-doc-type="Form"
                target="_blank"
              >
                LEED Request Form
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Submittal Documents (1)" dataTrackAccordion="Submittal Documents">
          <ul>
            <li>
              <a
                href="http://owenscorning.com/dms/10022026"
                data-track="file-download"
                data-track-file-name="ProPink L77 Loosefill Submittal Sheet"
                data-track-file-type="PDF"
                data-track-doc-type="Submittal Sheets"
                target="_blank"
                rel="noreferrer"
              >
                ProPink L77 Loosefill Submittal Sheet
              </a>
            </li>
          </ul>
        </Dropdown>
        <Dropdown label="Safety Data Sheets (1)" dataTrackAccordion="Safety Data Sheets">
          <ul>
            <li>
              <a
                href="https://sds.owenscorning.com/results?search%5Bcategory%5D=&search%5Bregion%5D=&search%5Blanguage_iso_code%5D=&search%5Bpublication_id%5D=&search%5Bshort_description%5D=propink+l77&search%5Bmanufacturer%5D="
                data-track="file-download"
                data-track-file-name="Search Safety Data Sheets (SDS/MSDS)"
                data-track-file-type="PDF"
                data-track-doc-type="Safety Data Sheet"
                target="_blank"
                rel="noreferrer"
              >
                Search Safety Data Sheets (SDS/MSDS)
              </a>
            </li>
          </ul>
        </Dropdown>
      </Container>
    </Section>
  </Container>
);

export const TechnicalInformation = () => (
  <Container maxWidth="1170">
    <Section anchor="technical-information">
      <h2>Technical Information</h2>

      <Dropdown label="Attics">
        <table css={technicalTable}>
          <thead>
            <tr>
              <th>R-VALUE</th>
              <th>MINIMUM INITIAL INSTALLED THICKNESS (IN)</th>
              <th>MINIMUM SETTLED THICKNESS&sup1;</th>
              <th>MAXIMUM COVERAGE PER BAG (SQ. FT.)</th>
              <th>MINIMUM BAGS PER 1000 SQ. FT.</th>
              <th>MINIMUM WEIGHT (LBS PER SQ. FT.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>13</td>
              <td>4.75</td>
              <td>4.75</td>
              <td>184.6</td>
              <td>5.4</td>
              <td>0.173</td>
            </tr>
            <tr>
              <td>19</td>
              <td>7.00</td>
              <td>7.00</td>
              <td>125.0</td>
              <td>8.0</td>
              <td>0.256</td>
            </tr>
            <tr>
              <td>22</td>
              <td>8.00</td>
              <td>8.00</td>
              <td>106.3</td>
              <td>9.4</td>
              <td>0.301</td>
            </tr>
            <tr>
              <td>26</td>
              <td>9.25</td>
              <td>9.25</td>
              <td>89.6</td>
              <td>11.2</td>
              <td>0.357</td>
            </tr>
            <tr>
              <td>30</td>
              <td>10.50</td>
              <td>10.50</td>
              <td>77.0</td>
              <td>13.0</td>
              <td>0.416</td>
            </tr>
            <tr>
              <td>38</td>
              <td>13.25</td>
              <td>13.25</td>
              <td>59.9</td>
              <td>16.7</td>
              <td>0.534</td>
            </tr>
            <tr>
              <td>44</td>
              <td>15.00</td>
              <td>15.00</td>
              <td>50.7</td>
              <td>19.7</td>
              <td>0.631</td>
            </tr>
            <tr>
              <td>49</td>
              <td>16.75</td>
              <td>16.75</td>
              <td>45.0</td>
              <td>22.2</td>
              <td>0.711</td>
            </tr>
            <tr>
              <td>60</td>
              <td>20.00</td>
              <td>20.00</td>
              <td>35.8</td>
              <td>28.0</td>
              <td>0.895</td>
            </tr>
          </tbody>
        </table>
      </Dropdown>
      <Dropdown label="Walls">
        <table css={technicalTable}>
          <thead>
            <tr>
              <th>R-VALUE</th>
              <th>FRAMING</th>
              <th>MINIMUM INITIAL INSTALLED THICKNESS (IN)</th>
              <th>INSTALLED DENSITY (LBS PER CU. FT.)</th>
              <th>MAXIMUM COVERAGE PER BAG (SQ. FT.)</th>
              <th>MINIMUM BAGS PER 1000 SQ. FT.</th>
              <th>MINIMUM WEIGHT (LBS PER SQ. FT.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>14</td>
              <td>2x4</td>
              <td>3.5</td>
              <td>1.25</td>
              <td>87.8</td>
              <td>11.4</td>
              <td>0.364</td>
            </tr>
            <tr>
              <td>15</td>
              <td>2x4</td>
              <td>3.5</td>
              <td>1.45</td>
              <td>75.7</td>
              <td>13.2</td>
              <td>0.423</td>
            </tr>
            <tr>
              <td>16</td>
              <td>2x4</td>
              <td>3.5</td>
              <td>2.20</td>
              <td>49.9</td>
              <td>20.1</td>
              <td>0.642</td>
            </tr>
            <tr>
              <td>22</td>
              <td>2x6</td>
              <td>5.5</td>
              <td>1.25</td>
              <td>55.9</td>
              <td>17.9</td>
              <td>0.573</td>
            </tr>
            <tr>
              <td>23</td>
              <td>2x6</td>
              <td>5.5</td>
              <td>1.35</td>
              <td>51.7</td>
              <td>19.3</td>
              <td>0.619</td>
            </tr>
            <tr>
              <td>24</td>
              <td>2x6</td>
              <td>5.5</td>
              <td>1.75</td>
              <td>40.0</td>
              <td>25.0</td>
              <td>0.802</td>
            </tr>
          </tbody>
        </table>
      </Dropdown>
      <Dropdown dataTrackAccordion="Cathedral Ceiling" label="Cathedral Ceiling&sup2;">
        <table css={technicalTable}>
          <thead>
            <tr>
              <th>R-VALUE</th>
              <th>FRAMING</th>
              <th>MINIMUM INITIAL INSTALLED THICKNESS (IN)</th>
              <th>INSTALLED DENSITY (LBS PER CU. FT.)</th>
              <th>MAXIMUM COVERAGE PER BAG (SQ. FT.)</th>
              <th>MINIMUM BAGS PER 1000 SQ. FT.</th>
              <th>MINIMUM WEIGHT (LBS PER SQ. FT.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>30</td>
              <td>2x8</td>
              <td>7.25</td>
              <td>1.35</td>
              <td>39.2</td>
              <td>25.5</td>
              <td>0.816</td>
            </tr>
            <tr>
              <td>38</td>
              <td>2x10</td>
              <td>9.25</td>
              <td>1.35</td>
              <td>30.8</td>
              <td>32.5</td>
              <td>1.041</td>
            </tr>
            <tr>
              <td>49</td>
              <td>2x12</td>
              <td>11.25</td>
              <td>1.85</td>
              <td>18.5</td>
              <td>54.2</td>
              <td>1.734</td>
            </tr>
          </tbody>
        </table>
      </Dropdown>
      <Dropdown label="Floors">
        <table css={technicalTable}>
          <thead>
            <tr>
              <th>R-VALUE</th>
              <th>FRAMING</th>
              <th>MINIMUM INITIAL INSTALLED THICKNESS (IN)</th>
              <th>INSTALLED DENSITY (LBS PER CU. FT.)</th>
              <th>MAXIMUM COVERAGE PER BAG (SQ. FT.)</th>
              <th>MINIMUM BAGS PER 1000 SQ. FT.</th>
              <th>MINIMUM WEIGHT (LBS PER SQ. FT.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>31</td>
              <td>2x8</td>
              <td>7.25</td>
              <td>1.8</td>
              <td>29.4</td>
              <td>34.0</td>
              <td>1.088</td>
            </tr>
            <tr>
              <td>40</td>
              <td>2x10</td>
              <td>9.25</td>
              <td>2.0</td>
              <td>20.8</td>
              <td>48.2</td>
              <td>1.542</td>
            </tr>
            <tr>
              <td>48</td>
              <td>2x12</td>
              <td>11.25</td>
              <td>1.9</td>
              <td>18</td>
              <td>55.7</td>
              <td>1.781</td>
            </tr>
          </tbody>
        </table>
      </Dropdown>
      <Dropdown label="Mid-Floors">
        <table css={technicalTable}>
          <thead>
            <tr>
              <th>MINIMUM INITIAL INSTALLED THICKNESS (IN)</th>
              <th>INSTALLED DENSITY (LBS PER CU. FT.)</th>
              <th>MAXIMUM COVERAGE PER BAG (SQ. FT.)</th>
              <th>MINIMUM BAGS PER 1000 SQ. FT.</th>
              <th>MINIMUM WEIGHT (LBS PER SQ. FT.)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>12</td>
              <td>0.7</td>
              <td>45.7</td>
              <td>21.9</td>
              <td>0.7</td>
            </tr>
            <tr>
              <td>12</td>
              <td>0.8</td>
              <td>40.0</td>
              <td>25.0</td>
              <td>0.8</td>
            </tr>
            <tr>
              <td>12</td>
              <td>0.9</td>
              <td>35.6</td>
              <td>28.1</td>
              <td>0.9</td>
            </tr>
            <tr>
              <td>14</td>
              <td>0.7</td>
              <td>39.2</td>
              <td>25.5</td>
              <td>0.8</td>
            </tr>
            <tr>
              <td>14</td>
              <td>0.8</td>
              <td>34.3</td>
              <td>29.2</td>
              <td>0.9</td>
            </tr>
            <tr>
              <td>14</td>
              <td>0.9</td>
              <td>30.5</td>
              <td>32.8</td>
              <td>1.1</td>
            </tr>
            <tr>
              <td>16</td>
              <td>0.7</td>
              <td>34.3</td>
              <td>29.2</td>
              <td>0.9</td>
            </tr>
            <tr>
              <td>16</td>
              <td>0.8</td>
              <td>30.0</td>
              <td>33.3</td>
              <td>1.1</td>
            </tr>
            <tr>
              <td>16</td>
              <td>0.9</td>
              <td>26.7</td>
              <td>37.5</td>
              <td>1.2</td>
            </tr>
            <tr>
              <td>18</td>
              <td>0.7</td>
              <td>30.5</td>
              <td>32.8</td>
              <td>1.1</td>
            </tr>
            <tr>
              <td>18</td>
              <td>0.8</td>
              <td>26.7</td>
              <td>37.5</td>
              <td>1.2</td>
            </tr>
            <tr>
              <td>18</td>
              <td>0.9</td>
              <td>23.7</td>
              <td>42.2</td>
              <td>1.4</td>
            </tr>
            <tr>
              <td>20</td>
              <td>0.7</td>
              <td>27.4</td>
              <td>36.5</td>
              <td>1.2</td>
            </tr>
            <tr>
              <td>20</td>
              <td>0.8</td>
              <td>24.0</td>
              <td>41.7</td>
              <td>1.3</td>
            </tr>
            <tr>
              <td>20</td>
              <td>0.9</td>
              <td>21.3</td>
              <td>46.9</td>
              <td>1.5</td>
            </tr>
            <tr>
              <td>22</td>
              <td>0.7</td>
              <td>24.9</td>
              <td>40.1</td>
              <td>1.3</td>
            </tr>
            <tr>
              <td>22</td>
              <td>0.8</td>
              <td>21.8</td>
              <td>45.8</td>
              <td>1.5</td>
            </tr>
            <tr>
              <td>22</td>
              <td>0.9</td>
              <td>19.4</td>
              <td>51.6</td>
              <td>1.7</td>
            </tr>
            <tr>
              <td>24</td>
              <td>0.7</td>
              <td>22.9</td>
              <td>43.8</td>
              <td>1.4</td>
            </tr>
            <tr>
              <td>24</td>
              <td>0.8</td>
              <td>20.0</td>
              <td>50.0</td>
              <td>1.6</td>
            </tr>
            <tr>
              <td>24</td>
              <td>0.9</td>
              <td>17.8</td>
              <td>56.3</td>
              <td>1.8</td>
            </tr>
            <tr>
              <td>30</td>
              <td>0.8</td>
              <td>16.0</td>
              <td>62.5</td>
              <td>2.0</td>
            </tr>
            <tr>
              <td>30</td>
              <td>0.9</td>
              <td>14.2</td>
              <td>70.3</td>
              <td>2.3</td>
            </tr>
            <tr>
              <td>30</td>
              <td>1.0</td>
              <td>12.8</td>
              <td>78.1</td>
              <td>2.5</td>
            </tr>
            <tr>
              <td>36</td>
              <td>0.8</td>
              <td>13.3</td>
              <td>75.0</td>
              <td>2.4</td>
            </tr>
            <tr>
              <td>36</td>
              <td>0.9</td>
              <td>11.9</td>
              <td>84.4</td>
              <td>2.7</td>
            </tr>
            <tr>
              <td>36</td>
              <td>1.0</td>
              <td>10.7</td>
              <td>93.8</td>
              <td>3.0</td>
            </tr>
            <tr>
              <td>40</td>
              <td>0.9</td>
              <td>10.7</td>
              <td>93.8</td>
              <td>3.0</td>
            </tr>
            <tr>
              <td>40</td>
              <td>1.0</td>
              <td>9.6</td>
              <td>104.2</td>
              <td>3.3</td>
            </tr>
            <tr>
              <td>40</td>
              <td>1.1</td>
              <td>8.7</td>
              <td>114.6</td>
              <td>3.7</td>
            </tr>
          </tbody>
        </table>
      </Dropdown>
    </Section>
  </Container>
);

const buttonArrow = css`
  &:after {
    font-family: 'icomoon';
    content: '';
    position: relative;
    display: block;
    align-self: center;
    margin-left: 20px;
  }
`;

// This one probably exists somewhere
export const FASRSplash = () => (
  <AttentionBarButtons heading="Find a Distributor Near Me">
    <Cta
      css={buttonArrow}
      href="/insulation/residential/distributors-near-me"
      color="black"
      data-track="find-a-professional"
      data-track-professional="residential-insulation-distributor"
    >
      Find a Distributor
    </Cta>
  </AttentionBarButtons>
);
